import * as Sentry from '@sentry/browser'

/**
 * Initialize Sentry for error tracking
 * @param {Object} config - Configuration object
 * @param {string} config.dsn - Sentry DSN
 * @param {string} config.environment - Environment name (development/production)
 * @param {string} config.release - Release version
 * @param {number} config.tracesSampleRate - Traces sample rate
 */
export function initSentry(config) {
  if (!config.dsn) {
    console.warn('[Sentry] DSN not configured, skipping initialization')
    return
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment || 'development',
    release: config.release || 'unknown',

    integrations: [
      Sentry.browserTracingIntegration({
        tracePropagationTargets: ['localhost', /^\//],
      }),
    ],

    tracesSampleRate: config.tracesSampleRate || 0.1,

    beforeSend(event, hint) {
      // 開発環境での誤検知除外
      if (config.environment === 'development') {
        if (event.exception?.values?.[0]?.value?.includes('ResizeObserver')) {
          return null
        }
      }

      // @sentry/browser v10.x の GlobalHandlers / WebWorker integration は、
      // mechanism.type に `auto.browser.<integration>.onunhandledrejection` 形式の
      // 長いハンドラ識別子を入れる。ドット区切り前提の末尾一致で判定することで
      // global_handlers / web_worker 等の integration variant を自動でカバーしつつ、
      // 短形式 'onunhandledrejection' も厳密一致で吸収する（手動 captureException で
      // 明示的に設定されるケース向けのフォールバック）。
      // 型ガードを先頭に置き、非文字列 (null / undefined / 数値等) が入ったときに
      // endsWith の呼び出しで例外が出ないようにしている。(GitHub #1420)
      const isUnhandledRejection = (mechanism) => {
        const type = mechanism?.type
        if (typeof type !== 'string') return false
        return type === 'onunhandledrejection' || type.endsWith('.onunhandledrejection')
      }

      // ネットワーク起因の TypeError (unhandledrejection) を除外
      //
      // 除外してよい理由:
      //   - ブラウザの fetch() がネットワーク不安定で失敗した際の汎用エラーであり、
      //     アプリケーションのバグではない
      //   - 発生源は Turbo 8 prefetch や GTM 経由のサードパーティスクリプト (Optemo 等) で、
      //     いずれもユーザー操作やアプリの主機能に影響しない
      //   - スタックトレースがない、またはライブラリ内部のみでアプリ固有のコードパスを
      //     追跡できないため、Sentry に残してもアクション不能
      //   (GitHub #1149, #1249)
      //
      // Chrome: "Failed to fetch", Firefox: "NetworkError when...", Safari: "Load failed"
      // NOTE: @sentry/browser v10.x の enhanceFetchErrorMessages (デフォルト有効) により
      //   "Failed to fetch" → "Failed to fetch (hostname)" のようにメッセージが拡張されるため、
      //   厳密一致ではなく前方一致で判定する (GitHub #1202)
      // NOTE: 一部の環境で value が "TypeError: Load failed" のように型名プレフィックス
      //   付きで渡されるケースがあるため、判定前に "{type}: " を剥がす (GitHub #1372)
      {
        const networkErrorMessages = [
          'Failed to fetch',
          'NetworkError when attempting to fetch resource',
          'Load failed',
        ]
        const isNetworkTypeError = (type, message) => {
          if (type !== 'TypeError') return false
          const prefix = `${type}: `
          const normalized = message.startsWith(prefix) ? message.slice(prefix.length) : message
          return networkErrorMessages.some(m => normalized.startsWith(m))
        }

        const firstException = event.exception?.values?.[0]
        if (isUnhandledRejection(firstException?.mechanism)) {
          // 同一オリジン: hint.originalException から直接判定
          // NOTE: 本ブランチでは .message が常に生メッセージなので isNetworkTypeError 内の
          //   型名プレフィックス正規化は no-op だが、下の cross-origin ブランチと
          //   同じ関数を使うことでロジックを共通化している
          if (
            hint?.originalException instanceof TypeError &&
            isNetworkTypeError('TypeError', hint.originalException.message)
          ) {
            return null
          }
          // クロスオリジン (GTM 経由等) や Turbo の Promise チェーン内で
          // instanceof TypeError が効かないケース:
          // アプリケーション固有のスタックフレーム (in_app: true) が
          // 含まれていなければ除外する。
          // Turbo prefetch やサードパーティ起因のネットワークエラーは弾きつつ、
          // 自前の Stimulus コントローラー等から発生した追跡可能なエラーは残す
          if (isNetworkTypeError(firstException?.type, firstException?.value || '')) {
            // NOTE: 本番では in_app が常に true になる (下の AbortError フィルタの NOTE 参照) ので、
            //   この分岐はスタックトレースを持つイベントを弾けない。同一オリジンの Turbo 等は
            //   上の instanceof 判定で弾けているので、すり抜けが観測されるまではこのままにする
            const frames = firstException?.stacktrace?.frames || []
            const hasAppFrame = frames.some(f => f.in_app)
            if (!hasAppFrame) {
              return null
            }
          }
        }
      }

      // ユーザー操作によるリクエスト中断（ページ遷移等）を除外 (GitHub #1177, #2230)
      // unhandledrejection 経由のみ除外し、captureException による意図的な送信は通す
      //
      // 発生源は Turbo 8 の hover prefetch（リンクにホバー → prefetch の fetch → 離脱で abort）や
      // GTM 経由のサードパーティスクリプト。アプリ側に AbortController は無く、追跡可能な
      // コードパスも持たないためアクション不能。
      //
      // NOTE: 型判定と mechanism 判定を「同一要素で AND」してはいけない (GitHub #2230)。
      //   reject 値が DOMException のとき Sentry SDK は values を 2 要素に分割し、
      //   型情報と mechanism が別要素に載る:
      //     [0] { type: 'AbortError', value: 'The user aborted a request.' }      ← mechanism なし
      //     [1] { type: 'Error', value: 'AbortError: ...', mechanism: {…UR} }     ← mechanism はこちら
      //   同一要素で AND すると some() で走査してもどの要素も両方を満たさず素通りする。
      //   実際に本番で ANDPAD-BUILDERWORK-API-1B としてすり抜けた。
      //   そのため「型を満たす要素が在るか」と「UR の要素が在るか」を独立に評価する。
      //
      // NOTE: value 側も見るのは、上記 [1] のように型名が value に畳み込まれた形
      //   （type が 'Error' になる）でも拾うため。mechanism 条件で守られているので
      //   captureException 経由の意図的な送信は通る。
      //
      // NOTE: スタックトレースの有無やフレームの in_app は見ない (GitHub #2429)。
      //   以前は「in_app フレームを持つイベントは、アプリ由来のエラーかもしれないので残す」
      //   としていたが、本番では in_app が常に true になり、スタックがあれば必ずすり抜けていた。
      //   ソースマップを Sentry にアップロードしていないため、自ドメインから配信される
      //   バンドル (Turbo を含む /vite/assets/*.js) も [native code] も、すべて in_app: true になる。
      //   実際に ANDPAD-BUILDERWORK-API-1G (Turbo の「戻る」でのキャンセル) と
      //   ANDPAD-BUILDERWORK-API-P ([native code] のフレーム) がすり抜けた。
      //
      //   in_app を見なくてよいのは、アプリのコード (app/javascript と画面に直接書いた <script>) に
      //   通信を中断する箇所 (AbortController の生成や abort の呼び出し) が無く、AbortError は
      //   ライブラリかサードパーティからしか来ないため。
      //   この前提は sentry.test.js で固定している。アプリで中断を使うようになったら、
      //   そのエラーを captureException で送るか、ここの条件を見直す。
      const isAbortError = (e) =>
        e.type === 'AbortError' || e.value?.startsWith('AbortError:')

      if (
        event.exception?.values?.some(isAbortError) &&
        event.exception?.values?.some(e => isUnhandledRejection(e.mechanism))
      ) {
        return null
      }

      // サードパーティ（Optemo）スクリプトの通信失敗を除外 (GitHub #1178, #1179)
      // NOTE: 上のネットワーク TypeError フィルタは unhandledrejection に限定しているため、
      //   それ以外の mechanism でホスト名付きエラーが来るケースはここで捕捉する
      if (event.exception?.values?.some(e =>
        e.type === 'TypeError' && e.value?.includes('prod-visitor.optemo.jp')
      )) {
        return null
      }

      // Optemo SDK 内部の thanksPage TypeError を除外 (GitHub #2019, 旧 #1711)
      //
      // 除外してよい理由:
      //   - 発生源は builderwork.jp で host される Optemo SDK (/js/connect.js) 内部。
      //     iOS Mobile Safari 等の fetch race で SDK の response object が null のまま
      //     `.thanksPage` を参照して落ちる、SDK 側の null guard 不足であり BW のバグではない
      //   - スタックトレース全 frame が connect.js 内で、BW 固有のコードパスは追跡不能
      //     (`thanksPage` は BW リポジトリに grep 0 件、Optemo SDK 固有のプロパティ名)
      //   - Users Impacted 0 で、ユーザー操作やアプリの主機能に影響しない
      //
      // 上の Optemo フィルタ (prod-visitor.optemo.jp) では弾けない理由:
      //   本エラーは exception の value にホスト名を含まず (optemo ドメインは breadcrumb 側
      //   にのみ出る)、value が `null is not an object (evaluating 'i.thanksPage')` /
      //   `Cannot read properties of null (reading 'thanksPage')` 等の文言のみのため。
      //   `thanksPage` は BW コードに存在しない固有名なので value 部分一致で誤検知しない。
      if (event.exception?.values?.some(e =>
        e.type === 'TypeError' && e.value?.includes('thanksPage')
      )) {
        return null
      }

      // ブラウザ拡張機能の通信エラーを除外 (GitHub #1328)
      // Safari Web Extension 等がタブ終了後に runtime.sendMessage() を呼び出した際の
      // unhandled rejection であり、アプリケーション起因ではない
      if (event.exception?.values?.some(e =>
        isUnhandledRejection(e.mechanism) &&
        e.value?.includes('runtime.sendMessage')
      )) {
        return null
      }

      // Safari 拡張機能のスクリプトだけで完結するエラーを除外 (GitHub #2457)
      //
      // 除外してよい理由:
      //   - `webkit-masked-url://hidden/` は、WebKit が Safari Web Extension のスクリプトの URL を
      //     Error.stack 上で隠すときの表記 (どの拡張機能が入っているかをページに知られないため)。
      //     BW のバンドル (/vite/assets/*) からは出ないので、フレームがすべてこの URL なら
      //     発生源は閲覧者の拡張機能であり、BW 側で追跡もアクションもできない
      //   - 実際に ANDPAD-BUILDERWORK-API-1C (`response.showSearchResults`) と
      //     ANDPAD-BUILDERWORK-API-1H (`response.msg`) が上がった。拡張機能が自分の裏側に
      //     問い合わせ、返事が undefined のまま参照して落ちた形 (#2454)
      //
      // 上の runtime.sendMessage フィルタで弾けない理由:
      //   問い合わせ自体は成功しているので value に runtime.sendMessage が出ず、文言も拡張機能
      //   ごとに違う。そのため文言ではなくフレームの URL で判定する。
      //
      // NOTE: in_app は見ない。本番ではこのフレームも in_app: true になる (上の AbortError
      //   フィルタの NOTE 参照)。BW のフレームが 1 つでも混ざれば送るので、拡張機能から BW の
      //   コードが呼ばれて落ちたものは残る。スタックの無いイベントは判定材料が無いので送る。
      {
        const frames = event.exception?.values?.flatMap(e => e.stacktrace?.frames ?? []) ?? []
        if (frames.length > 0 && frames.every(f => f.filename?.startsWith('webkit-masked-url://'))) {
          return null
        }
      }

      // 暗号資産ウォレット系ブラウザ拡張の unhandled rejection を除外 (GitHub #2136)
      //
      // 除外してよい理由:
      //   - reject された値が Error ではなく web3 (EIP-1193) プロバイダのエラーオブジェクト
      //     `{ code, message }`（例: `{ code: 4001, message: "wallet must has at least one
      //     account" }`）。ウォレット拡張が全ページで window に注入し、アカウント未接続時等に
      //     投げた rejection が window レベルで未処理のまま onunhandledrejection に捕捉される
      //   - BW は web3/wallet SDK を一切使っていない（ethereum/web3/metamask は grep 0 件、
      //     唯一の "wallet" は応募完了画面の UI アイコン名で無関係）。stacktrace も無く追跡不能
      //   - 特定ユーザーの拡張環境依存で、アプリの主機能・ユーザー操作に影響しない
      //
      // 上のフィルタで弾けない理由:
      //   exception.value は Sentry が生成する "Object captured as promise rejection with
      //   keys: code, message" で、ウォレット固有の文言を含まない。ウォレットの文言は reject
      //   された生オブジェクト側にのみ出るため、hint.originalException（同一 tick で拾える通常
      //   ケース）と event.extra.__serialized__（SDK が正規化した写し）の message を参照する。
      //
      // 新種のウォレット文言が観測されたら walletRejectionSignatures に追記する
      // （Rule of Three で数が増えたら EIP-1193 code 等での一般化を検討）。
      {
        const walletRejectionSignatures = [
          'wallet must has at least one account',
        ]
        const firstException = event.exception?.values?.[0]
        if (isUnhandledRejection(firstException?.mechanism)) {
          const rejected = hint?.originalException ?? event.extra?.__serialized__
          const message = typeof rejected?.message === 'string' ? rejected.message : ''
          if (walletRejectionSignatures.some(sig => message.includes(sig))) {
            return null
          }
        }
      }

      // ユーザー情報の匿名化
      if (event.user) {
        const { id } = event.user
        event.user = { id }
      }

      return event
    },

    beforeBreadcrumb(breadcrumb, hint) {
      // センシティブなURLパラメータをフィルタリング
      if (breadcrumb.category === 'navigation') {
        breadcrumb.data = breadcrumb.data || {}
        const rawUrl = breadcrumb.data.to || window.location.href
        const url = new URL(rawUrl, window.location.origin)
        const sensitiveParams = ['token', 'password', 'api_key', 'email']

        sensitiveParams.forEach(param => {
          if (url.searchParams.has(param)) {
            url.searchParams.set(param, '[Filtered]')
          }
        })

        breadcrumb.data.to = url.toString()
      }

      return breadcrumb
    },

    initialScope: {
      tags: {
        framework: 'stimulus',
        bundler: 'vite'
      }
    }
  })

  console.info(`[Sentry] Initialized for ${config.environment}`)
}

/**
 * グローバルエラーハンドラー（Sentry SDK が自動処理しないTurbo固有エラー）
 * error / unhandledrejection は Sentry の GlobalHandlers integration が処理するため登録不要
 */
export function setupGlobalErrorHandlers() {
  // Turbo特有のエラーハンドリング（Sentry SDKが自動処理しないため手動キャプチャ）
  document.addEventListener('turbo:frame-missing', (event) => {
    console.error('[Turbo Frame Missing]', event.detail)
    Sentry.captureException(new Error(`Turbo frame missing: ${event.detail.response.url}`))
  })
}

/**
 * HTMLのmetaタグからSentry設定を読み取り、初期化とグローバルエラーハンドラーを設定する
 */
export function initSentryFromMetaTags() {
  const config = {
    dsn: document.querySelector('meta[name="sentry-dsn"]')?.content,
    environment: document.querySelector('meta[name="sentry-environment"]')?.content,
    release: document.querySelector('meta[name="sentry-release"]')?.content,
    tracesSampleRate: parseFloat(document.querySelector('meta[name="sentry-traces-sample-rate"]')?.content || '0.1')
  }
  initSentry(config)
  setupGlobalErrorHandlers()
}

/**
 * StimulusアプリケーションのエラーハンドリングをSentryに連携する
 * @param {Application} application - Stimulus Application インスタンス
 */
export function setupStimulusErrorHandling(application) {
  application.handleError = (error, message, detail) => {
    console.error('[Stimulus Error]', message, error, detail)
    Sentry.captureException(error, {
      contexts: {
        stimulus: {
          message,
          detail
        }
      }
    })
  }
}
